import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Paper, Tabs, Tab, Button, Skeleton, useTheme
} from '@mui/material';
import { ContentCopy, School, MenuBook } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { teachingLoadsApi } from '../../services/sessions/teachingLoadsApi';
import { useModulePermissions } from '../../hooks/useModulePermissions';

import ClassTeachersTab from './ClassTeachersTab';
import SubjectTeachersTab from './SubjectTeachersTab';
import CopyFromPreviousSessionDialog from './CopyFromPreviousSessionDialog';
import { TeacherOption } from './TeacherPickerSelect';

interface Props {
  sessionId: string;
  sessionName: string;
}

type InnerTabKey = 'class' | 'subject';

const TeachingLoadsTab: React.FC<Props> = ({ sessionId, sessionName }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const writable = canWrite('teaching_loads');

  const [innerTab, setInnerTab] = useState<InnerTabKey>('class');

  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(true);

  const [copyOpen, setCopyOpen] = useState(false);
  // Bumped after a successful copy so child tabs reload from the server.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setTeachersLoading(true);
    (async () => {
      try {
        // Server already restricts to isTeacher-role holders and gates the
        // route on teaching_loads.read, so no users.read scope is required.
        const opts = await teachingLoadsApi.listTeachers();
        if (cancelled) return;
        setTeachers(opts as TeacherOption[]);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load teachers', { variant: 'error' });
      } finally {
        if (!cancelled) setTeachersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  // Children are remounted on reloadToken bump so they re-fetch cleanly.
  const childKey = useMemo(() => `${sessionId}::${reloadToken}`, [sessionId, reloadToken]);

  return (
    <Box>
      {/* Inner tabs + bulk action. Button sits as a sibling of the tabs
          Paper so its outline doesn't collide with the Paper's right edge. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            px: 1
          }}
        >
          <Tabs
            value={innerTab}
            onChange={(_, v: InnerTabKey) => setInnerTab(v)}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 44, gap: 0.75 }
            }}
          >
            <Tab value="class"   icon={<School fontSize="small" />}   iconPosition="start" label="Class Teachers" />
            <Tab value="subject" icon={<MenuBook fontSize="small" />} iconPosition="start" label="Subject Teachers" />
          </Tabs>
        </Paper>

        {writable && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopy fontSize="small" />}
            onClick={() => setCopyOpen(true)}
            sx={{ borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Copy from previous session
          </Button>
        )}
      </Stack>

      {/* Body */}
      {teachersLoading ? (
        <Skeleton variant="rounded" height={320} />
      ) : innerTab === 'class' ? (
        <ClassTeachersTab key={childKey} sessionId={sessionId} teachers={teachers} />
      ) : (
        <SubjectTeachersTab key={childKey} sessionId={sessionId} teachers={teachers} />
      )}

      <CopyFromPreviousSessionDialog
        open={copyOpen}
        targetSessionId={sessionId}
        targetSessionName={sessionName}
        onClose={() => setCopyOpen(false)}
        onCopied={() => setReloadToken(t => t + 1)}
      />
    </Box>
  );
};

export default TeachingLoadsTab;
